import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { GetTotalUnreadMessageCount } from "@/api/user";

/** Message badge + SignalR push state（对齐 Vue2 store/modules/message） */
export const useMessageStore = defineStore("message", () => {
  const homeMsg = ref(0);
  const homeNoticeMsg = ref(0);
  const homeWarnMsg = ref(0);
  const homeRemindMsg = ref(0);
  const noReadMsg = ref(0);

  const sysMsg = ref({
    dialog: false,
    mid: 0,
    title: "",
    content: "",
    remind: false,
  });
  const moodleMsg = ref({
    dialog: false,
    mid: 0,
    title: "",
    content: "",
  });
  const classMsg = ref({
    dialog: false,
    processing: false,
    mid: 0,
    title: "",
    content: "",
  });
  const paypalMsg = ref({
    dialog: false,
    processing: false,
    salaryAccount: "",
  });
  const selfScheduleRemindMsg = ref({
    isShow: false,
    tips: "",
  });
  /** Vue2 message.attendanceWarning — 出勤预警弹层 */
  const attendanceWarning = ref({
    dialog: false,
    CLID: null,
    WarnType: null,
  });
  /** Vue2 message.absentMsg — 课程出勤确认 */
  const absentMsg = ref({
    dialog: false,
    classTime: "",
    id: "",
    count: 0,
  });

  const noticeTabLabel = computed(() =>
    homeNoticeMsg.value > 0 ? `Notice (${homeNoticeMsg.value})` : "Notice"
  );
  const warningTabLabel = computed(() =>
    homeWarnMsg.value > 0 ? `Warning (${homeWarnMsg.value})` : "Warning"
  );
  const remindTabLabel = computed(() =>
    homeRemindMsg.value > 0 ? `Remind (${homeRemindMsg.value})` : "Remind"
  );

  function setHomeMsg(n) {
    homeMsg.value = Number(n) || 0;
  }
  function setHomeNoticeMsg(n) {
    homeNoticeMsg.value = Number(n) || 0;
  }
  function setHomeWarnMsg(n) {
    homeWarnMsg.value = Number(n) || 0;
  }
  function setHomeRemindMsg(n) {
    homeRemindMsg.value = Number(n) || 0;
  }

  function decUnread(messageType) {
    if (homeMsg.value > 0) homeMsg.value -= 1;
    if (messageType === "notice" && homeNoticeMsg.value > 0) homeNoticeMsg.value -= 1;
    if (messageType === "warning" && homeWarnMsg.value > 0) homeWarnMsg.value -= 1;
    if (messageType === "remind" && homeRemindMsg.value > 0) homeRemindMsg.value -= 1;
  }

  async function fetchUnreadTotals() {
    try {
      const res = await GetTotalUnreadMessageCount();
      const body = res?.data?.Body ?? res?.data ?? res ?? {};
      setHomeMsg(body.Total);
      setHomeNoticeMsg(body.Notice);
      setHomeWarnMsg(body.Warning);
      setHomeRemindMsg(body.Remind);
    } catch (_) {
      /* ignore */
    }
  }

  function addSysMsg(msg) {
    if (!msg) return;
    sysMsg.value = {
      dialog: true,
      remind: !!msg.remind,
      mid: msg.mid ?? msg.Mid ?? 0,
      title: msg.title ?? msg.Title ?? "",
      content: msg.content ?? msg.Content ?? "",
      ...msg,
    };
  }

  function closeSysMsg() {
    sysMsg.value = { ...sysMsg.value, dialog: false };
  }

  function addMoodleMsg(msg) {
    if (!msg) return;
    moodleMsg.value = {
      dialog: true,
      mid: msg.mid ?? msg.Mid ?? 0,
      title: msg.title ?? msg.Title ?? "",
      content: msg.content ?? msg.Content ?? "",
      ...msg,
    };
  }

  function addClassMsg(msg) {
    if (!msg) return;
    classMsg.value = {
      mid: msg.mid ?? msg.Mid ?? 0,
      title: msg.title ?? msg.Title ?? "",
      content: msg.content ?? msg.Content ?? "",
      ...msg,
      dialog: !classMsg.value.processing,
      processing: classMsg.value.processing,
    };
  }

  function closeClassMsg(processing = false) {
    classMsg.value = {
      ...classMsg.value,
      dialog: false,
      processing: !!processing,
    };
  }

  function openClassMsg() {
    classMsg.value = { ...classMsg.value, dialog: true, processing: false };
  }

  function addPaypalMsg(msg) {
    if (!msg) return;
    paypalMsg.value = {
      ...msg,
      dialog: !paypalMsg.value.processing,
      processing: paypalMsg.value.processing,
    };
  }

  function closePaypalMsg() {
    paypalMsg.value = { ...paypalMsg.value, dialog: false };
  }

  function addNoReadMsg(total) {
    const n =
      typeof total === "object" && total != null
        ? Number(total.total ?? total.Total ?? 0)
        : Number(total) || 0;
    noReadMsg.value = n;
    // 顶栏角标与现网铃铛同源刷新
    if (n >= 0) setHomeMsg(n);
  }

  function addSelfScheduleRemindMsg(msg) {
    if (!msg) return;
    selfScheduleRemindMsg.value = {
      isShow: !!(msg.IsShow ?? msg.isShow),
      tips: msg.Tips || msg.tips || "",
    };
  }

  function addAttendanceWarning(msg) {
    if (!msg) return;
    attendanceWarning.value = {
      dialog: msg.dialog !== undefined ? !!msg.dialog : true,
      CLID: msg.clid || msg.CLID || null,
      WarnType: msg.warnType || msg.WarnType || null,
    };
  }

  function closeAttendanceWarning() {
    attendanceWarning.value = {
      ...attendanceWarning.value,
      dialog: false,
    };
  }

  function addAbsentMsg(msg) {
    if (!msg) return;
    absentMsg.value = {
      dialog: true,
      classTime: msg.classTime || msg.ClassTimeStr || "",
      id: msg.id || msg.Id || "",
      count: msg.count || msg.Count || 0,
    };
  }

  function closeAbsentMsg() {
    absentMsg.value = { ...absentMsg.value, dialog: false };
  }

  return {
    homeMsg,
    homeNoticeMsg,
    homeWarnMsg,
    homeRemindMsg,
    noReadMsg,
    sysMsg,
    moodleMsg,
    classMsg,
    paypalMsg,
    selfScheduleRemindMsg,
    attendanceWarning,
    absentMsg,
    noticeTabLabel,
    warningTabLabel,
    remindTabLabel,
    setHomeMsg,
    setHomeNoticeMsg,
    setHomeWarnMsg,
    setHomeRemindMsg,
    decUnread,
    fetchUnreadTotals,
    addSysMsg,
    closeSysMsg,
    addMoodleMsg,
    addClassMsg,
    closeClassMsg,
    openClassMsg,
    addPaypalMsg,
    closePaypalMsg,
    addNoReadMsg,
    addSelfScheduleRemindMsg,
    addAttendanceWarning,
    closeAttendanceWarning,
    addAbsentMsg,
    closeAbsentMsg,
  };
});
